import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, MapPin } from 'lucide-react';
import { locations } from '../data/menuData';
import { Star, Triangle, Zigzag } from './MemphisShapes';

const Expansion = () => {
  return (
    <section 
      id="expansion"
      data-testid="expansion-section"
      className="min-h-screen bg-santomato-yellow py-16 md:py-24 relative overflow-hidden"
    >
      {/* Decorative shapes */}
      <Star className="absolute top-16 right-8 hidden md:block" color="#FF2A2A" size={50} />
      <Triangle className="absolute bottom-24 left-12 hidden md:block" color="#000000" size={40} />
      <Zigzag className="absolute top-1/2 right-4 hidden lg:block" color="#FF2A2A" width={100} />

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <Rocket size={60} className="text-santomato-red mx-auto" strokeWidth={2.5} />
          </motion.div>
          <h2 
            className="font-fredoka font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-black uppercase mb-4"
            data-testid="expansion-title"
          >
            QUESTO È SOLO
            <br />
            <span className="text-santomato-red">L'INIZIO</span>
          </h2>
          <p className="font-nunito font-bold text-lg md:text-xl text-gray-800 max-w-2xl mx-auto">
            Santomato è pensato per crescere. Stesso format, stessa qualità, nuove sedi in arrivo.
          </p>
        </motion.div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {locations.map((location, index) => (
            <motion.div
              key={location.id}
              initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ scale: 1.02 }}
              className={`card-brutal p-6 md:p-8 ${location.isOpen ? 'bg-white' : 'bg-gray-100'}`}
              data-testid={`location-${location.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`rounded-full p-3 ${location.isOpen ? 'bg-santomato-red' : 'bg-gray-400'}`}>
                  <MapPin size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-fredoka font-bold text-2xl md:text-3xl text-black uppercase">
                      {location.name}
                    </h3>
                    {location.isOpen ? (
                      <span className="bg-green-500 text-white font-nunito font-bold text-xs px-3 py-1 rounded-full">
                        APERTO
                      </span>
                    ) : (
                      <span className="bg-santomato-red text-white font-nunito font-bold text-xs px-3 py-1 rounded-full animate-pulse">
                        COMING SOON
                      </span>
                    )}
                  </div>
                  <p className="font-nunito text-gray-700 mb-1">{location.address}</p>
                  <p className="font-nunito font-bold text-gray-900">{location.city}</p>
                  {location.hostedBy && (
                    <p className="font-nunito text-sm text-santomato-red font-bold mt-1">{location.hostedBy}</p>
                  )}

                  {location.isOpen ? (
                    <>
                      {location.phone && (
                        <p className="font-nunito text-sm text-gray-600 mt-3">
                          Tel: <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="text-santomato-red hover:underline">{location.phone}</a>
                        </p>
                      )}
                      {location.hours && (
                        <p className="font-nunito text-sm text-gray-600">{location.hours}</p>
                      )}
                      {location.note && (
                        <p className="font-nunito text-xs text-gray-500 italic">{location.note}</p>
                      )}
                      {location.email && (
                        <p className="font-nunito text-sm text-gray-600">
                          Email: <a href={`mailto:${location.email}`} className="text-santomato-red hover:underline">{location.email}</a>
                        </p>
                      )}
                      {location.logo && (
                        <img
                          src={location.logo}
                          alt={`Logo ${location.name}`}
                          className="mt-4 w-32 h-32 object-contain rounded-2xl border-4 border-black bg-[#F2EBDD] p-2"
                          data-testid={`location-logo-${location.id}`}
                        />
                      )}
                      <div className="flex flex-wrap gap-3 mt-4">
                        {location.mapUrl && (
                          <a
                            href={location.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block btn-brutal bg-santomato-red text-white font-fredoka text-sm px-6 py-2 rounded-full"
                            data-testid={`location-map-${location.id}`}
                          >
                            INDICAZIONI
                          </a>
                        )}
                        {location.website && (
                          <a
                            href={location.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block btn-brutal bg-black text-santomato-yellow font-fredoka text-sm px-6 py-2 rounded-full"
                            data-testid={`location-website-${location.id}`}
                          >
                            VISITA IL SITO
                          </a>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="font-fredoka text-santomato-red text-lg mt-3">
                      {location.openingDate}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Expansion;
